import React, { useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { Box } from '@mui/material';
import { AnyAction } from '@reduxjs/toolkit';
import { useSnackbar } from 'notistack';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

import {
    ContextActionType,
    EmptyBox,
    HeaderActionButton,
    HeaderListSkeleton,
    ListHeader,
    ListSkeleton,
    SkeletonLoader,
    useDumbActionsContext,
} from '@europrocurement/flexy-components';
import {
    DossierOpenSearch,
    DossierOpenSearchSelector,
    DOSSIERS_OPEN_SEARCH_SLICE_NAME,
    useFormaliteService,
    useSociete,
} from '@europrocurement/l2d-domain';
import {
    DossierActionContext,
    dossiersBasePath,
    DossierTable,
    useDefaultContextActions,
} from '@europrocurement/l2d-modules';
import { DataSource } from '@europrocurement/l2d-redux-utils';
import { createFormalityFolioIcon, FaOptionIcon } from '@europrocurement/l2d-icons';

import { useInitialLoadingState } from '@europrocurement/l2d-hooks';
import { useDossierTable } from '../../hooks';

/**
 * Page de listing des dossiers de formalités.
 */
const ListeDossiers: React.FunctionComponent = function () {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();

    const datasource: DataSource<DossierOpenSearch> = useSelector(DossierOpenSearchSelector);
    const { isInitialLoading, isInError } = useInitialLoadingState(datasource);

    const xIdSociete = useSociete();
    const { getOpenSearchDossiers } = useFormaliteService();
    const { updatingDossier } = useContext(DossierActionContext);
    const { backContextActions } = useDefaultContextActions();

    const { columns, filters, preFilters, rowActions } = useDossierTable();
    const [filtersInitialized, setFiltersInitialized] = useState<boolean>(false);

    const breadcrumbPath = useMemo(
        () => [
            {
                link: '/accueil',
                icon: {
                    icon: ['fasl', 'house'] as IconProp,
                    sx: { display: 'flex', alignItems: 'center' },
                },
            },
            { link: '/dossiers', path: 'Formalités' },
            'Dossiers',
        ],
        [],
    );

    // Initialisation des filtres et chargement des données
    useEffect(() => {
        dispatch({
            type: `${DOSSIERS_OPEN_SEARCH_SLICE_NAME}/setmainPagination`,
            payload: { page: 1, itemsPerPage: 10 },
        });
        dispatch({
            type: `${DOSSIERS_OPEN_SEARCH_SLICE_NAME}/setmainFilter`,
            payload: { key: 'societeId', value: [xIdSociete] },
        });
        dispatch(getOpenSearchDossiers({}) as unknown as AnyAction);
        setFiltersInitialized(true);
    }, [dispatch, getOpenSearchDossiers, xIdSociete, updatingDossier]);

    const handleRowClick = useCallback(
        ({ id }: DossierOpenSearch) => {
            navigate(`/${dossiersBasePath}/${id}`);
        },
        [navigate],
    );

    const handleCreateDossier = useCallback(() => {
        enqueueSnackbar('Création nouveau dossier', { variant: 'error' });
        // navigate(parcoursFormalitePathResolver('stepFormalite'));
    }, [enqueueSnackbar]);

    const contextActions: ContextActionType[] = useMemo(
        () => [
            {
                name: 'Nouveau dossier',
                icon: <FaOptionIcon {...createFormalityFolioIcon.props} />,
                action: handleCreateDossier,
            },
            ...backContextActions,
        ],
        [backContextActions, handleCreateDossier],
    );

    useDumbActionsContext(contextActions);

    const errorContent = useMemo(
        () => (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100vh',
                    width: '100%',
                }}
            >
                <EmptyBox
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'space-evenly',
                        height: '25%',
                        width: '50%',
                    }}
                    text="Une erreur s'est produite lors de la récupération des dossiers."
                    subText="Veuillez recharger la page. Si le problème persiste, contactez votre administrateur."
                />
            </Box>
        ),
        [],
    );

    // Attendre l'initialisation des filtres avant d'afficher quoi que ce soit
    if (!filtersInitialized) {
        return <ListSkeleton columns={6} />;
    }

    return (
        <SkeletonLoader
            isLoading={isInitialLoading}
            skeleton={
                <>
                    <HeaderListSkeleton />
                    <ListSkeleton
                        showSearch
                        columns={6}
                    />
                </>
            }
            isInError={isInError}
            errorChildren={errorContent}
        >
            <ListHeader
                breadcrumbPath={breadcrumbPath}
                actions={
                    <HeaderActionButton
                        label="Créer un nouveau dossier de formalité"
                        icon={createFormalityFolioIcon}
                        onClick={handleCreateDossier}
                        actionType="modal"
                        disabled
                    />
                }
            />

            {datasource && (
                <DossierTable
                    datasource={datasource}
                    columns={columns}
                    filters={filters}
                    preFilters={preFilters}
                    rowActions={rowActions}
                    onRowClick={handleRowClick}
                />
            )}
        </SkeletonLoader>
    );
};

export default ListeDossiers;
